import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'lodash';
import { browserHistory } from 'react-router';
import { SegmentUiElements, SegmentPageActionTypes } from '@foxcorp/delta-web-analytics';
import { SHOW_STATE_CLIP, SHOW_STATE_UPCOMING } from 'helpers/BadgeHelper';
import {
  helperGetRelativeUrl,
  redirectToExternalUrl,
  LAST_SESSION_PATH,
} from 'helpers/RouteHelper';
import { noop } from 'helpers/GlobalHelper';
import { COLLECTION_THUMBNAIL_LIVE_IMAGE } from 'helpers/ImageHelper';
import {
  COLLECTION_TYPE_GENRE,
  COLLECTION_TYPE_LIVE,
  COLLECTION_TYPE_LOCAL_CLIPS,
  isInternalLink,
} from 'helpers/ContentHelper';
import { setSessionStorageItem } from 'helpers/Storage';
import { isVideoLive } from 'components/VideoPlayerGlobal/Helper';
import { FAVORITE_PLUS_HOVER } from 'components/Favorite';
import Badge from 'components/Badge';
import {
  ActionButtons,
  Detail,
  MobilePlayButton,
  NetworkBubble,
  Overlay,
  PlayButton,
  ProgressBar,
  Subtitle,
  TileImage,
  TileTitle,
} from '../../../components';
import {
  isMobileDevice,
  playButtonClickProcessor,
  titleProcessor,
} from '../../../helpers/processorHelper';
import Style from '../../../Styles/LiveTile.module.scss';

const LiveTile = props => {
  const {
    account,
    altText,
    badge,
    breakpoint,
    callSign,
    collectionType,
    disableHoverStateActionButtons,
    endDate,
    favoritesManager,
    gridIndex,
    gridTitle,
    hoverImage,
    image,
    isAnonymous,
    isRedirect,
    isSeries,
    isSeriesDetailAvailable,
    itemIndex,
    link,
    name,
    network,
    onTileClick,
    onTitleClick,
    onTileMouseDown,
    originalItem,
    overlayProps,
    percentWatched,
    recommendationIdData,
    scaleFromLeft,
    scaleFromRight,
    segmentPlaylistButtonAction,
    seriesName,
    seriesScreenUrl,
    showCode,
    showLiveProgressBar,
    startDate,
    subtitle,
    subtitleLines,
    subtitleText,
    target,
    theme,
    title,
    titleLink,
    titleText,
    truncateSubtitle,
    type,
    videoType,
  } = props;
  const [favorited, setFavorited] = useState(false);
  const titleLinkVal = titleLink || link;
  const imageContainerClasses = [Style.imageContainer, Style.maintainAspectRatio];
  if (scaleFromLeft) imageContainerClasses.push(Style.scaleFromLeft);
  if (scaleFromRight) imageContainerClasses.push(Style.scaleFromRight);

  const onSubtitleOnClick = useCallback(() => {
    if (onTileClick) {
      onTileClick({
        account,
        name,
        originalItem,
        percentWatched,
        recommendationIdData,
        subtitleText,
        titleText,
        uiElement: SegmentUiElements.SERIES_EPISODE,
      });
    }
  }, [
    onTileClick,
    originalItem,
    name,
    percentWatched,
    account,
    subtitleText,
    titleText,
    recommendationIdData,
  ]);

  const onTileTitleClick = useCallback(() => {
    setSessionStorageItem({ key: LAST_SESSION_PATH, value: window.location.pathname });
    onTileClick({ uiElement: SegmentUiElements.SERIES_EPISODE });
    if (onTitleClick) {
      onTitleClick({
        account,
        gridIndex,
        gridTitle,
        itemIndex,
        name,
        network,
        originalItem,
        percentWatched,
        recommendationIdData,
        subtitleText,
        title,
        titleText,
        uiElement: SegmentUiElements.SERIES_TITLE,
      });
    }
  }, [
    account,
    gridIndex,
    gridTitle,
    itemIndex,
    name,
    network,
    onTileClick,
    onTitleClick,
    originalItem,
    percentWatched,
    recommendationIdData,
    subtitleText,
    title,
    titleText,
  ]);

  const onMoreInfoClick = () => {
    segmentPlaylistButtonAction({
      actionType: SegmentPageActionTypes.MORE_EPISODES,
      uiElement: SegmentUiElements.INFO,
    });
  };

  const titleOptions = {
    ...titleProcessor(props),
    link: titleLinkVal,
    onClick: onTileTitleClick,
    onTileMouseDown,
    Style,
  };
  const isMobile = isMobileDevice(breakpoint);
  const isNotUpcoming = badge !== SHOW_STATE_UPCOMING;
  const favorites = _.get(favoritesManager, 'favorites');
  const moreInfoText = videoType === 'clip' ? 'More Info' : 'More Episodes';
  const isLocalClip = collectionType === COLLECTION_TYPE_LOCAL_CLIPS;
  const seriesUrl = seriesScreenUrl ? helperGetRelativeUrl({ url: seriesScreenUrl }) : titleLinkVal;
  const isGenre = collectionType === COLLECTION_TYPE_GENRE;
  const isLiveCollection = collectionType === COLLECTION_TYPE_LIVE;
  const isFavorited = favorited || _.find(favorites, ({ favoriteID }) => favoriteID === showCode);
  const currentLink = isSeries ? titleLinkVal : link;
  const shouldRenderPlayButton =
    !isMobile && !isGenre && (!isLiveCollection || (isLiveCollection && isVideoLive(originalItem)));

  const favoriteData = {
    ...(overlayProps?.favoriteData || {}),
    titleText: seriesName,
    subtitleText,
  };
  const playButtonClick = () => {
    playButtonClickProcessor({ type, props })();
    if (isInternalLink(currentLink)) {
      browserHistory.push(currentLink);
    } else {
      redirectToExternalUrl(currentLink);
    }
  };

  return (
    <div className={theme.tile}>
      <div
        className={classNames(imageContainerClasses)}
        onClick={() => {
          if (isMobile) {
            playButtonClick();
          }
        }}
      >
        <TileImage
          breakpoint={breakpoint}
          context={COLLECTION_THUMBNAIL_LIVE_IMAGE}
          image={image || hoverImage}
          altText={altText}
          theme={Style}
        />
        <div className={Style.liveTileShadow} />
        <Badge theme={Style} badge={badge} />
        <ProgressBar
          endDate={endDate}
          percentWatched={percentWatched}
          showProgressBar={showLiveProgressBar}
          startDate={startDate}
        />
        <Overlay theme={Style} render={!isMobile}>
          <ActionButtons
            favoriteData={favoriteData}
            favorites={favorites}
            favoritesManager={favoritesManager}
            handleSetFavorite={fav => setFavorited(!!fav)}
            hasSeries={!isSeries && isSeriesDetailAvailable !== false}
            hoverStateWatchlistText={isFavorited ? 'Added to Watchlist' : 'Add to Watchlist'}
            iconType={FAVORITE_PLUS_HOVER}
            isOnlyInfo={!favorites || badge === SHOW_STATE_CLIP}
            isOnlyWatchList={isSeries || isSeriesDetailAvailable === false}
            link={seriesUrl}
            moreInfoAria={`${moreInfoText} ${seriesName}`}
            moreInfoText={moreInfoText}
            onMoreInfoClick={onMoreInfoClick}
            render={!isGenre && !disableHoverStateActionButtons && !isLocalClip && !isAnonymous}
            segmentPlaylistButtonAction={segmentPlaylistButtonAction}
          />
          <PlayButton
            playButtonClick={playButtonClick}
            render={shouldRenderPlayButton}
            theme={Style}
          />
        </Overlay>
        {isNotUpcoming && (
          <MobilePlayButton theme={Style} render={isMobile} playButtonClick={playButtonClick} />
        )}
      </div>
      <Detail theme={Style}>
        <div className={classNames([Style.info, Style.infoExtended])}>
          <NetworkBubble callSign={callSign} link={link} showNetworkIcon />
          <div className={Style.rightDetails}>
            <TileTitle {...titleOptions} />
            <Subtitle
              isDisabled={false}
              isRedirect={isRedirect}
              link={link}
              onClick={onSubtitleOnClick}
              onTileMouseDown={onTileMouseDown}
              subtitle={subtitle}
              subtitleLines={subtitleLines}
              target={target}
              theme={Style}
              truncateSubtitle={truncateSubtitle}
            />
          </div>
        </div>
      </Detail>
    </div>
  );
};

export default LiveTile;

LiveTile.propTypes = {
  account: PropTypes.shape({}),
  altText: PropTypes.string,
  badge: PropTypes.string,
  breakpoint: PropTypes.string.isRequired,
  callSign: PropTypes.string,
  collectionType: PropTypes.string,
  disableHoverStateActionButtons: PropTypes.bool,
  endDate: PropTypes.string,
  favoritesManager: PropTypes.shape({}),
  gridIndex: PropTypes.number,
  gridTitle: PropTypes.string,
  hoverImage: PropTypes.string,
  image: PropTypes.string,
  isAnonymous: PropTypes.bool,
  isRedirect: PropTypes.bool,
  isSeries: PropTypes.bool,
  isSeriesDetailAvailable: PropTypes.bool,
  itemIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  link: PropTypes.string,
  name: PropTypes.string,
  network: PropTypes.string,
  onTileClick: PropTypes.func,
  onTitleClick: PropTypes.func,
  onTileMouseDown: PropTypes.func,
  originalItem: PropTypes.shape({}),
  overlayProps: PropTypes.shape({}),
  percentWatched: PropTypes.number,
  recommendationIdData: PropTypes.oneOfType([PropTypes.node, PropTypes.shape({})]),
  scaleFromLeft: PropTypes.bool,
  scaleFromRight: PropTypes.bool,
  segmentPlaylistButtonAction: PropTypes.func,
  seriesName: PropTypes.string,
  seriesScreenUrl: PropTypes.string,
  showCode: PropTypes.string,
  showLiveProgressBar: PropTypes.bool,
  startDate: PropTypes.string,
  subtitle: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  subtitleLines: PropTypes.number,
  subtitleText: PropTypes.string,
  target: PropTypes.string,
  theme: PropTypes.shape({}).isRequired,
  title: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  titleLink: PropTypes.string,
  titleText: PropTypes.string,
  truncateSubtitle: PropTypes.bool,
  type: PropTypes.string,
  videoType: PropTypes.string,
};

LiveTile.defaultProps = {
  account: {},
  altText: null,
  badge: null,
  callSign: null,
  collectionType: null,
  disableHoverStateActionButtons: false,
  endDate: null,
  favoritesManager: {},
  gridIndex: null,
  gridTitle: null,
  hoverImage: null,
  image: null,
  isAnonymous: false,
  isRedirect: false,
  isSeries: false,
  isSeriesDetailAvailable: false,
  itemIndex: null,
  link: null,
  name: null,
  network: null,
  onTileClick: noop,
  onTitleClick: noop,
  onTileMouseDown: noop,
  originalItem: null,
  overlayProps: null,
  percentWatched: null,
  recommendationIdData: null,
  scaleFromLeft: false,
  scaleFromRight: false,
  segmentPlaylistButtonAction: noop,
  seriesName: null,
  seriesScreenUrl: null,
  showCode: null,
  showLiveProgressBar: false,
  startDate: null,
  subtitle: null,
  subtitleLines: null,
  subtitleText: null,
  target: null,
  title: null,
  titleLink: null,
  titleText: null,
  truncateSubtitle: false,
  type: null,
  videoType: null,
};
